import type { ErrorEvent } from "@sentry/react";

import { normalizeApiPath } from "./normalize-api-path";

/**
 * Caps the error events Sentry's httpClientIntegration sends for failed API
 * responses, so an API outage doesn't flood Sentry from every open tab.
 *
 * Limits apply per browser tab:
 *   - The same failure (status + method + path) is sent at most once per window
 *   - At most MAX_EVENTS_PER_WINDOW HTTP error events are sent per window
 *
 * Other errors are never affected.
 */
const WINDOW_MS = 5 * 60 * 1000;
const MAX_EVENTS_PER_WINDOW = 5;

const sentTimestamps: number[] = [];
const lastSentByKey = new Map<string, number>();

/** True for error events from Sentry's httpClientIntegration. */
export const isHttpClientEvent = (event: ErrorEvent) =>
  event.exception?.values?.some(
    (value) => value.mechanism?.type === "http.client",
  ) ?? false;

const getFailureKey = (event: ErrorEvent) => {
  const status = event.contexts?.response?.status_code ?? "unknown";
  const method = event.request?.method ?? "GET";
  const path = normalizeApiPath(event.request?.url ?? "");

  return `${status} ${method} ${path}`;
};

/** Returns the event to send it, or null to drop it. Use in `beforeSend`. */
export const limitHttpClientErrors = (event: ErrorEvent): ErrorEvent | null => {
  if (!isHttpClientEvent(event)) {
    return event;
  }

  const now = Date.now();
  while (sentTimestamps.length && now - sentTimestamps[0] > WINDOW_MS) {
    sentTimestamps.shift();
  }

  const key = getFailureKey(event);
  const lastSent = lastSentByKey.get(key);
  if (lastSent !== undefined && now - lastSent < WINDOW_MS) {
    return null;
  }
  if (sentTimestamps.length >= MAX_EVENTS_PER_WINDOW) {
    return null;
  }

  sentTimestamps.push(now);
  lastSentByKey.set(key, now);
  return event;
};
